import { useCallback, useEffect, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, isNull, isString } from "lodash";

import locationService from "@plannel/services/data/location-service";
import CustomTooltip from "@plannel/components/aggrid/CustomTooltip";
import DataState from "@plannel/components/aggrid/DataState"
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import Dialog from "@plannel/components/Dialog"
import { AddButton, RemoveButton } from "@plannel/components/ActionIconButton";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { SD, MAX_AVG, NOR_DIST, NOR_DIST_SUP } = CodeUtil.ipPolicyCd;
const IP_POLICY_LIST = [SD, MAX_AVG, NOR_DIST, NOR_DIST_SUP];
const INV_CLASS_CD_LIST = CodeUtil.dpABCConfig;
const ANALYSIS_FILTER_LOCATION = "ANALYSIS_FILTER_LOCATION";
const MAX_NOR_DIST_VALUE = 99.99;

const IpBulkValueUpdateModal = (props) => {
  const { t, updateTargets, service, modalOpen, setModalOpen, selectedScenario, retrieve, setSnackInfo, setProgressOn } = props;
  const reduxDispatch = useDispatch();

  const [locations, setLocations] = useState([]);
  const [abcXyzClass, setAbcXyzClass] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const allLocation = { locCd: null, name: t("all") };
  const integerColumns = ["invPolicyVal"];
  const isNumericColumn = params => integerColumns.includes(params.data?.updateTarget);
  const { snackBarMessageColor } = ColorUtil;

  const numberValueFormatter = (val, digits) => {
    const value = NumberUtil.formatNumber(val, { maximumFractionDigits: digits });
    return Number(value);
  }

  const decimalValueFormatter = (val, digits) => {
    if (val === null || val === undefined || val === '') return null;
    const multiplier = Math.pow(10, digits);
    return Math.trunc(val * multiplier) / multiplier;
  }

  const setAbcXyzClassData = () => {
    const abcXyzClassData = [t("all"), ...INV_CLASS_CD_LIST];
    setAbcXyzClass(abcXyzClassData);
  }

  const setLocationData = () => {
    const locationReduxViewState = reduxUtil.getViewState(ANALYSIS_FILTER_LOCATION);
    if (locationReduxViewState?.location) {
      setLocations([allLocation, ...locationReduxViewState.location]);
    } else {
      let locationDatas = [];
      locationService.getLookup()
        .then(response => {
          const { results } = response.data;
          locationDatas = results;
          setLocations([allLocation, ...results]);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const locationReduxState = reduxUtil.createObjectState(ANALYSIS_FILTER_LOCATION, { location: locationDatas });
          reduxDispatch(reduxUtil.updateViewState(locationReduxState));
        });
    }
  }


  const bulkValueUpdate = () => {
    gridApi.stopEditing();

    const updateData = DataState.getAllStateData(gridApi);
    for (const data of updateData) {
      const { updateTarget, updateValue, targetInvPolicyCd } = data;

      if (isEmpty(updateTarget)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedUpdateTarget") });
        return;
      }

      if (isEqual(updateTarget, "invPolicyCd") && isEmpty(updateValue)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_MissingInput") });
        return;
      }

      if (isEqual(updateTarget, "invPolicyVal") && isEmpty(targetInvPolicyCd)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_MissingInput") });
        return;
      }

      if (integerColumns.includes(updateTarget)) {
        if (isEqual(updateValue, "")) {
          data.updateValue = null;
        } else if (!isNull(updateValue)) {
          if (isString(updateValue)) {
            const trimValue = updateValue.trim();
            data.updateValue = isEqual(trimValue, "") ? null : trimValue; 
          } else {
            let updateTargetValue = Number(updateValue);

            if (targetInvPolicyCd === CodeUtil.ipPolicyCd.NOR_DIST || targetInvPolicyCd === CodeUtil.ipPolicyCd.NOR_DIST_SUP) {
              if (updateTargetValue > MAX_NOR_DIST_VALUE) {
                updateTargetValue = MAX_NOR_DIST_VALUE;
              } else {
                updateTargetValue = decimalValueFormatter(updateTargetValue, 2);
              }              
            } else {
              updateTargetValue = Number(updateValue);
            }

            data.updateValue = updateTargetValue;
          }
        }
      }

      if (isNull(data.location.locCd)) {
        data.location = null;
      }

      if (isEqual(data.invClassCd, t("all"))) {
        data.invClassCd = null;
      }
    }

    setProgressOn(true);

    service.bulkValueUpdate(updateData)
      .then(response => {
        const { data } = response;
        const planVersion = isEmpty(data?.planVersion) ? data : data?.planVersionId;

        setProgressOn(false);
        setModalOpen(false);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        
        if (planVersion && !isEmpty(planVersion)) {
          retrieve(planVersion.id);
        } else {
          retrieve();
        }

      })
      .catch(e => {
        const { data } = e.response;
        const errorMessage = data?.message ? data.message : "MSG_SaveFailed";
        const content = data?.variables ? t(errorMessage, { itemCodes: data.variables }) : t(errorMessage);

        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content });
        setProgressOn(false);
        debug(e);
      });
  }

  const handleClose = action => {
    if (isEqual(action, "cancel")) {
      const updateData = DataState.getAllStateData(gridApi);
      if (!isEmpty(updateData)) {
        setDialogInfo({ open: true, title: "close", content: t("MSG_ConfirmToClose") });
      } else {
        setModalOpen(false);
      }
    } else if (isEqual(action, "save")) {
      bulkValueUpdate();
    }
  }

  const objectComparator = (value1, value2, property) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];
    if (val1 === val2) {
      return 0;
    } else if (val1 > val2) {
      return 1;
    }
    return -1;
  }

  const textValueFormatter = params => {
    return params.value?.name ? params.value.name : "";
  }

  const updateValueFormatter = params => {
    const updateTarget = params?.node?.data?.updateTarget;

    if (isEqual(updateTarget, "invPolicyCd")) {
      return GridUtils.gridValueL10N(params.value);
    }

    return params.value;
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const CalendarTooltip = useCallback(({ data }) => {
    if (isEqual(data?.updateTarget, "orderCalendar")) {
      return (
        <div className="custom-tooltip">
          <p>{t("MSG_NotSetOrderCalendar")}</p>
          <p>{t("MSG_ReCalcOpInvDays")}</p>
        </div>
      );
    }

    return null;
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const BooleanRenderer = useCallback((params) => {
    const checkboxClickEvent = (e, param) => {
      param.node.setDataValue(param.column, !param.value);
    }

    return (
      <div>
        <input 
          type="checkbox" 
          checked={params.value || false}
          onChange={(e) => checkboxClickEvent(e, params)} 
          disabled={!params.colDef.editable}
        />
      </div>
    ); 
   });

  const updateValueCellRenderer = (params) => { // set dynamic cell renderer type 
    const updateTarget = params?.data?.updateTarget;

    if (isEqual(updateTarget, "safetyStockFlg") || isEqual(updateTarget, "salesFlg") || isEqual(updateTarget, "shipmentsFlg") || isEqual(updateTarget, "cycleInvFlg")) {
      return BooleanRenderer(params);
    }

    return <div>{updateValueFormatter(params)}</div>;
  }

  const cellEditorSelector = (params) => {
    const selectCellTargets = ["invPolicyCd"];
    const { updateTarget } = params.data;

    if (selectCellTargets.includes(updateTarget)) {
      return { component: 'agRichSelectCellEditor', popup: true };
    }

    return { component: 'agTextCellEditor' };
  }

  const cellEditorParams = (params) => {
    const { updateTarget } = params.data;

    if (isEqual(updateTarget, "invPolicyCd")) {
      return {
        formatValue: (params) => {
          const value = params?.value || params;

          return GridUtils.gridValueL10N(value);
        },
        values: IP_POLICY_LIST
      }
    }

    return { valueFormatter: "", values: [] }
  }

  const onCellClicked = (params) => {
    const { data, column, node, value } = params;
    const colId = column.getColId();
    const { updateTarget } = data;

    if (isEqual(colId, "updateValue") && (isEqual(updateTarget, "safetyStockFlg") || isEqual(updateTarget, "salesFlg") || isEqual(updateTarget, "shipmentsFlg") || isEqual(updateTarget, "cycleInvFlg"))) {
      node.setDataValue(column, !value);
    }
  }

  const onCellValueChanged = (params) => {
    const { column, node, data, oldValue, newValue } = params;
    const colId = column.getColId();

  

    if (!isEqual(oldValue, newValue)) {
      if (isEqual(colId, "updateTarget")) {
        node.setDataValue("targetInvPolicyCd", null);
      }

      if (isEqual(colId, "updateTarget")) {
        node.setDataValue("updateValue", null);
        node.setDataValue("additionalValue", null);

        if (isEqual(data.updateTarget, "safetyStockFlg") || isEqual(data.updateTarget, "salesFlg") || isEqual(data.updateTarget, "shipmentsFlg") || isEqual(data.updateTarget, "cycleInvFlg")) {
          node.setDataValue("updateValue", true);
        }
      }

      if (isEqual(colId, "updateValue") || isEqual(colId, "additionalValue")) {
        if (isNumericColumn(params)) {
          if (newValue === "" || newValue === null || newValue === undefined) {
            node.setDataValue(colId, null);
            return;
          }

          let numberValue = Number(newValue);
          if (!isFinite(numberValue) || isNaN(numberValue) || isNull(numberValue)) {
            node.setDataValue(colId, null);
          } else {
            if (integerColumns.includes(data.updateTarget)) {
              const updateTargetInvPolicyCd = data.targetInvPolicyCd;
              if (updateTargetInvPolicyCd === CodeUtil.ipPolicyCd.NOR_DIST || updateTargetInvPolicyCd === CodeUtil.ipPolicyCd.NOR_DIST_SUP) {
                if (numberValue > MAX_NOR_DIST_VALUE) {
                  numberValue = MAX_NOR_DIST_VALUE;
                }
                numberValue = decimalValueFormatter(numberValue, 2);
              } else {
                numberValue = numberValueFormatter(numberValue, 0);
              }
            }
            
            node.setDataValue(colId, numberValue);
          }
        }
      }
    }

    DefaultGridSetting({}).onCellValueChanged(params);
  }

  const handleDialog = (action) => {
    if (isEqual(action, "CLOSE")) {
      setModalOpen(false);
    }
  }

  const dynamicEditable = (params) => {
    const { data, column } = params;
    const colId = column.getColId();

    if (isEqual(colId, "updateValue")) {  // Boolean ?�?��? ?�집 불�? (체크박스로만 변�?
      const booleanTargets = ["safetyStockFlg", "salesFlg", "shipmentsFlg", "cycleInvFlg"];

      return !booleanTargets.includes(data.updateTarget);
    }

    return isEqual(data.updateTarget, "invPolicyVal");
  }

  const columnDefs = [
    { headerName: "version", colId: "planVersionName", type: ["defaultValueFormatter", "defaultValueParser"], field: "planVersionId", width: 150, hide: true, suppressColumnsToolPanel: true },
    {
      headerName: "location", field: "location", width: 130,
      cellEditor: "agRichSelectCellEditor",
      type: ["defaultValueParser"],
      cellEditorPopup: true,
      cellEditorParams: {
        valueFormatter: textValueFormatter,
        values: locations
      },
      valueFormatter: textValueFormatter,
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    {
      headerName: "invClassCd", field: "invClassCd", width: 120,
      cellRenderer: AbcXyzChip,
      cellEditor: "agRichSelectCellEditor",
      type: ["defaultValueParser"],
      cellEditorPopup: true,
      cellEditorParams: {
        values: abcXyzClass
      },
      comparator: (value1, value2) => objectComparator(value1, value2, "name")
    },
    {
      headerName: "updateTarget", colId: "updateTarget", field: "updateTarget", width: 130,
      cellEditor: "agRichSelectCellEditor",
      type: ["defaultValueParser"],
      cellEditorPopup: true, cellEditorParams: { values: updateTargets },
      valueFormatter: params => params.value === "cycleInvFlg" ? t("cycleStock") : t(params.value)
    },
    {
      headerName: "targetInvPolicyCd", colId: "targetInvPolicyCd", field: "targetInvPolicyCd", width: 130,
      cellEditor: "agRichSelectCellEditor",
      type: ["defaultValueParser"],
      cellEditorPopup: true, headerTooltip: t("MSG_EnterOnlyPolicyVal"), tooltipComponent: CustomTooltip,
      cellEditorParams : {
        values: IP_POLICY_LIST.filter(item => item !== MAX_AVG)
      }, valueFormatter: params => {
        return GridUtils.gridValueL10N(params.value);
      }, cellRendererParams: { setSnackInfo }, editable: dynamicEditable
    },
    {
      headerName: "setValue", colId: "updateValue", field: "updateValue", width: 200, flex: 1,
      cellRenderer: updateValueCellRenderer, cellEditorSelector, cellEditorParams,
      valueFormatter: updateValueFormatter, onCellClicked, cellRendererParams: { setSnackInfo },
      tooltipComponent: CalendarTooltip, tooltipField: "updateTarget", editable: dynamicEditable
    },
    {
      headerName: "additionalValue", colId: "additionalValue", field: "additionalValue", width: 200, flex: 1, type: ["numberColumn"],
      headerTooltip: t("MSG_EnterOnlyPolicyVal"), tooltipComponent: CustomTooltip,
      cellEditorSelector: params => {
        if (isNumericColumn(params)) {
          return cellEditorSelector(params);
        }

        return { component: 'agTextCellEditor' };
      },
      valueFormatter: params => {
        if (isNumericColumn(params)) {
          return updateValueFormatter(params);
        }

        return params.value;
      },
      editable: dynamicEditable
    }
  ];

  const addRow = () => {
    const newRow = {
      planVersionId: selectedScenario.id ? selectedScenario.id : null,
      location: allLocation,
      invClassCd: t("all"),
      updateTarget: null,
      updateValue: null,
      additionalValue: null,
      targetInvPolicyCd: null
    };

    const rowCount = gridApi.getDisplayedRowCount();
    DataState.setDataState(GridUtils.addRow(gridApi, newRow, rowCount, false));
  }

  const deleteRows = () => {
    const deleteNodes = gridApi.getSelectedNodes();
    if (!isEmpty(deleteNodes)) {
      DataState.deleteState(gridApi, deleteNodes);
    }
  }

  const onGridReady = params => {
    setGridApi(params.api);
    DataState.initialize(params.api);

    setLocationData();
    setAbcXyzClassData();
  }

  useEffect(() => {
    if (modalOpen) {
      if (gridApi) {
        addRow();
      }
    } else {
      setGridApi(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, gridApi]);

  return (
    <>
      <BaseConfigModal
        open={modalOpen}
        onClose={() => handleClose("cancel")}
        title={t("bulkValueUpdate")}
        onSave={() => handleClose("save")}
        width={932}>
          <Box sx={{ textAlign: "right", mr: 1.5 }}>
            <AddButton onClick={addRow} />
            <RemoveButton onClick={() => {
              gridApi.stopEditing();
              deleteRows();
            }} />
          </Box>
          <Box className="ag-theme-balham grid-area" mb={1} sx={{ height: "calc(100vh - 770px)", minHeight: "300px", minWidth: "inherit" }}>
            <AgGridReact
              columnDefs={GridUtils.getColumnDefs(columnDefs)}
              {...DefaultGridSetting({ title: "bulkValueUpdate" })}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              rowSelection={{ mode: 'multiRow', enableClickSelection: false, checkboxes: true, headerCheckbox: true, selectAll: 'filtered' }}
              tooltipShowDelay={100}
              tooltipHideDelay={5000}
            />
          </Box>
      </BaseConfigModal>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  );
}

export default withTranslation()(IpBulkValueUpdateModal);